import MachineIcon from '@/buyers/components/Frame/Icons/MachineIcon'
import ImpersonationBanner from '@/buyers/components/ImpersonationBanner'
import useSession from '@/dealers/hooks/useSession'
import Link from '@/gf/components/Link'
import RocketLayout from '@/gf/components/RocketLayout'
import { MessagesIcon } from '@/gf/components/RocketLayout/HeaderActions'
import OrgDetails from '@/gf/components/RocketLayout/OrgDetails'
import { HeaderActions, NavItemGroup } from '@/gf/components/RocketLayout/types'
import type { AppLink } from '@/gf/components/RocketLayout/UserMenu'
import useConfig from '@/gf/hooks/useConfig'
import { Breadcrumbs } from '@/types'
import {
  ChartBarSquareIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  PaperAirplaneIcon,
  RectangleGroupIcon,
  UserIcon,
} from '@heroicons/react/24/outline'
import { ReactNode, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUnreadMessagesQuery } from '../_gen/gql'
import Urls from '../modules/Urls'
import DevMenu from './Frame/DevMenu'
import BoxIcon from './Frame/Icons/BoxIcon'
import LimitedUserFrame from './Frame/LimitedUserFrame'
import SearchResults from './Frame/SearchResults'
import SiteUrls from '@/retail/modules/SiteUrls'

type Props = {
  children: ReactNode
  breadcrumbs?: Breadcrumbs
  className?: string
}

const Frame = ({ children, breadcrumbs, className }: Props) => {
  const navigate = useNavigate()
  const session = useSession()
  const config = useConfig()
  const { user, fullExperience, store } = session
  const { data, startPolling, stopPolling } = useUnreadMessagesQuery({ fetchPolicy: 'no-cache' })
  const unreadMessagesCount = data?.conversations.pagination.totalResults ?? 0

  useEffect(() => {
    startPolling(30000)
    return () => stopPolling()
  }, [])

  const navItems: NavItemGroup[] = useMemo(
    () => [
      {
        name: 'main',
        items: [
          {
            children: (
              <>
                <RectangleGroupIcon className="h-5 w-5 inline-block " /> Dashboard
              </>
            ),
            href: '/dashboard',
          },

          {
            children: (
              <>
                <PaperAirplaneIcon className="h-5 w-5 inline-block rotate-90" /> Requests
              </>
            ),
            href: '/rfqs',
          },
          {
            children: (
              <>
                <BoxIcon className="inline-block" />
                Catalog
              </>
            ),
            href: '/products',
          },
        ],
      },
      {
        name: 'management',
        items: [
          {
            children: (
              <>
                <MachineIcon className="inline-block" /> Customers
              </>
            ),
            href: '/accounts',
          },
          {
            children: (
              <>
                <UserIcon className="h-5 w-5 inline-block" /> Contacts
              </>
            ),
            href: '/customers',
          },
          {
            children: (
              <>
                <ChartBarSquareIcon className="h-5 w-5 inline-block" /> Reports
              </>
            ),
            href: '/reports',
          },
        ],
      },
      {
        name: 'app-settings',
        items: [
          {
            children: (
              <>
                <Cog6ToothIcon className="h-5 w-5 inline-block" /> Settings
              </>
            ),
            href: '/settings',
          },
          {
            children: (
              <>
                <CreditCardIcon className="h-5 w-5 inline-block" /> Plan & Billing
              </>
            ),
            href: Urls.planAndBilling(),
          },
          {
            children: (
              <>
                <CurrencyDollarIcon className="h-5 w-5 inline-block" /> Wallet
              </>
            ),
            href: '/wallet',
          },
        ],
      },
    ],
    []
  )

  const headerActions: HeaderActions = [
    {
      label: 'Inbox',
      onClick: () => navigate(Urls.inbox()),
      icon: <MessagesIcon />,
      count: unreadMessagesCount,
      testId: 'unread-count',
    },
  ]

  const appLinks = useMemo<AppLink[]>(() => {
    const links: AppLink[] = []
    if (store?.showPartsHub) links.push({ name: 'Parts Hub', href: config.buyersUrl })
    return links
  }, [store?.showPartsHub, config.buyersUrl])

  if (!fullExperience) {
    return <LimitedUserFrame children={children} />
  }

  const subscription = {
    loading: false,
    isProPlan: store?.subscription?.isPro ?? false,
    productName: 'Sales Hub',
    upgradePlan: (
      <Link.T to={Urls.planAndBilling()} className="font-medium text-sm">
        Upgrade
      </Link.T>
    ),
    upgradePlanHref: SiteUrls.DEALER_SCHEDULE_DEMO_URL,
    userCanUpgradePlan: true,
    inTrialPeriod: store?.subscription?.inTrialPeriod ?? false,
    trialExpirationDate: store?.subscription?.trialExpiresAt ?? undefined,
  }

  return (
    <RocketLayout
      orgDetails={
        <OrgDetails
          companyDetails={{
            logoUrl: store?.logo?.url ?? null,
            name: store?.name ?? '',
            updateHref: '/settings/organization',
          }}
          subscriptionDetails={subscription}
        />
      }
      user={{ name: user.displayName }}
      subscription={subscription}
      navAction={{
        href: '/orders/new',
        label: 'Create Quote',
      }}
      headerActions={headerActions}
      navItems={navItems}
      appLinks={appLinks}
      className={className ?? ''}
      breadcrumbs={breadcrumbs}
      renderSearch={({ onClose, searchTerm }) => (
        <SearchResults searchTerm={searchTerm} onItemSelected={onClose} />
      )}
      header={<DevMenu />}
      topBanner={session.originalAdminName ? <ImpersonationBanner session={session} /> : undefined}
      lightBackground
    >
      {children}
    </RocketLayout>
  )
}

export default Frame
